// @flow
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import routes, { assetUrl } from '~/src/config/routes.js';
import cx from 'classnames';
import SVG from 'react-inlinesvg';
import get from 'lodash/get';
import difference from 'lodash/difference';
import Select from 'react-select';
import trackEvent from '~/src/services/analytics';
import { Collapse } from 'react-collapse';

import t from '~/src/services/i18n';

import type { FieldSet, Field } from '~/src/config/form';
import Tooltip from '~/src/components/Tooltip';
import RadioButtons from '~/src/components/RadioButtons';
import ColorPicker from '~/src/components/ColorPicker';
import ToggleSwitch from '~/src/components/ToggleSwitch';
import Range from '~/src/components/Range';

import './FormPanel.scss';

declare var cloudinary: any;

type Props = {
  formConfig: Array<FieldSet>,
  setFormValue: Function,
  formState: Object,
  isMobile?: boolean
};

type State = {
  expanded: Array<string>,
};
@observer
class FormPanel extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      expanded:
        props.formConfig.length > 0
          ? ['widgetParametersTitle', 'mainViewerTitle']
          : [],
    };
  }

  validateDependencies(field: Field) {
    let isValid = true;
    if (field.dependOnFields) {
      isValid = field.dependOnFields.every((depField) => {
        if (typeof depField.value == 'string') {
          return depField.value
            .split(',')
            .some((val) => get(this.props.formState, depField.name) === val);
        } else {
          return get(this.props.formState, depField.name) === depField.value;
        }
      });
    }
    return isValid;
  }

  handleChange(field: Field, val: any) {
    const { setFormValue } = this.props;
    switch (field.propertyName) {
      case 'layout':
        setFormValue('mode', val.mode, 'displayProps');
        setFormValue('columns', val.columns, 'displayProps');
        trackEvent({
          action: 'edit layout',
          label: val.mode + '-' + val.columns,
        });
        break;
      case 'level':
        // Round to 'step' decimal order
        // i.e. if step is 0.1 will round 3.888 to 3.9
        var decimal = (field.step.toString().split('.')[1] || []).length;
        var value = parseFloat(val.toFixed(decimal));
        setFormValue(field.propertyName, value, field.group);
        break;
      default:
        setFormValue(field.propertyName, val, field.group);
        break;
    }
  }

  renderFiledByType(field: Field) {
    let fieldComp;

    const value = field.group
      ? this.props.formState[field.group] &&
        this.props.formState[field.group][field.propertyName]
        ? this.props.formState[field.group][field.propertyName]
        : ''
      : this.props.formState[field.propertyName];

    // Disabled fields, can be done in 2 ways:
    // 1. Static - pass a `true` boolean as the field.disabled value
    // 2. Dynamic - pass a function as the field.enabled value, this function will get
    //    the forms state as input and should return true/false.
    let disabled =
      field.disabled || (field.enabled && !field.enabled(this.props.formState));

    switch (field.type) {
      case 'select':
      case 'radio': {
        let options;
        if (field.enum) {
          const enums = cloudinary.galleryWidgetInfo.ENUMS[field.enum];
          options = Object.keys(enums).map((option) => ({
            value: enums[option],
            label: enums[option],
            icon: field.icons ? field.icons[enums[option]] : 'Icons/' + enums[option],
          }));
        } else {
          options =
            field.options &&
            field.options.map((option, i) => ({
              value: option.value || option,
              label: option.label || option,
              subLabel: option.subLabel,
              icon: option.icon || option,
              key: option.key || option.value || i,
            }));
        }

        if (field.icons && field.type === 'select') {
          options = options && options.map((o) => ({
            ...o,
            label: <div className="label-wrapper">
              <SVG src={assetUrl(field.icons[o.label])} />
              {o.label || o}
            </div>
          }));
        }

        if (
          field.propertyName == 'carouselLocation' &&
          this.props.formState.layout == 'expanded1'
        ) {
          // Carousel location should only be selectable (non-greyed out) in classic and expanded-1-col.
          // Values for expanded-1-col should be only left and right
          options =
            options &&
            options.filter(
              (o) => typeof o.value === 'string' && (o.value == 'left' || o.value == 'right')
            );
        }

        if (this.props.isMobile) {
          if (field.group == 'zoomProps' && field.propertyName == 'type') {
            options =
              options && options.filter((o) => o.value !== 'flyout');
          }
          if (field.group == 'zoomProps' && field.propertyName == 'trigger') {
            options =
              options && options.filter((o) => o.value !== 'hover');
            disabled = true;
          }
        }

        let curOption =
          options && options.find((option) => option.value === value);

        fieldComp =
          field.type === 'select' ? (
            <Select
              onChange={(value) => {
                this.handleChange(field, value ? value.value : '');
              }}
              options={options}
              value={curOption}
              isClearable={'clearable' in field ? field.clearable : true}
              isDisabled={disabled}
              className="select"
              classNamePrefix
            />
          ) : (
            <RadioButtons
              value={value ? value : ''}
              items={options || []}
              setValue={(name, value) => this.handleChange(field, value)}
              propertyName={field.propertyName}
              disabled={disabled}
            />
          );
        break;
      }
      case 'color':
        fieldComp = (
          <ColorPicker
            name={t(field.label)}
            color={value}
            setColor={(name, value) => this.handleChange(field, value)}
            propertyName={field.propertyName}
            disabled={disabled}
            tooltip={field.tooltip}
          />
        );
        break;
      case 'number':
        fieldComp = (
          <Range
            value={value ? value : 0}
            disabled={disabled}
            max={field.max}
            min={field.min ? field.min : 0}
            step={field.step}
            onChange={(value) => {
              this.handleChange(field, value);
            }}
          />
        );
        break;
      case 'checkbox':
        fieldComp = (
          <ToggleSwitch
            value={
              field.onValue ? (value === field.onValue ? true : false) : !!value
            }
            setValue={(name, value) =>
              this.handleChange(
                field,
                value
                  ? field.onValue
                    ? field.onValue
                    : true
                  : field.offValue
                    ? field.offValue
                    : false
              )
            }
            propertyName={field.propertyName}
            disabled={disabled}
            onValue={field.onValue}
            offValue={field.offValue}
          />
        );
        break;
      case 'label':
        fieldComp = <h5 className="widget-label">{t(field.label)}</h5>;
        break;
      default:
        //@todo: handle text area for custom transformation.
        fieldComp = (
          <input
            disabled={disabled}
            type={field.type}
            value={value}
            onChange={(e) =>
              this.handleChange(
                field,
                field.type !== 'number'
                  ? e.target.value
                  : Number(e.target.value)
              )
            }
            {...field.inputProps}
          />
        );
        break;
    }
    return (
      <div
        key={field.propertyName}
        className={cx(
          'field-container',
          'field-type-' + field.type,
          'field-prop-' + field.propertyName,
          { 'field-disabled': disabled }
        )}
      >
        {field.type !== 'color' && field.type !== 'label' && !field.onValue && (
          <label>{t(field.label)}</label>
        )}
        {fieldComp}
        {field.tooltip && (
          <Tooltip content={field.tooltip}>
            <i className="icon-info-circled info" />
          </Tooltip>
        )}
        {field.propertyName == 'layout' && <p>{t('layoutMobileText')}</p>}
      </div>
    );
  }

  render() {
    const { formConfig } = this.props;
    const { expanded } = this.state;
    return (
      <div className="form-container">
        {formConfig.map((widget) => {
          const isOpened =
            widget.hideTitle || expanded.includes(widget.fieldSetTitle);
          return (
            <div
              className={cx('widget', 'widget-' + widget.fieldSetTitle)}
              key={widget.fieldSetTitle}
            >
              {!widget.hideTitle && (
                <div className="widget-header">
                  <h5 className="widget-label">{t(widget.fieldSetTitle)}</h5>
                  {widget.tooltip && (
                    <Tooltip content={t(widget.tooltip)}>
                      <a
                        className="docs-link"
                        target="_blank"
                        href={
                          routes.docReference + '#' + (widget.fieldSetUrl || '')
                        }
                        onClick={() => {
                          trackEvent({
                            action: 'learn more',
                            label:
                              'clicked on ' +
                              t(widget.fieldSetTitle, {}) +
                              ' to learn more about product gallery'
                          });
                        }}
                      >
                        <SVG src={assetUrl('Icons/learn-more')} />
                      </a>
                    </Tooltip>
                  )}
                  <Tooltip
                    content={
                      isOpened ? t('collapseSection') : t('expandSection')
                    }
                  >
                    <button
                      className={cx('collapse-icon', {
                        'flip-icon': !isOpened,
                      })}
                      onClick={() => {
                        trackEvent({
                          action: 'show-hide',
                          label:
                            (isOpened ? 'close ' : 'open ') +
                            t(widget.fieldSetTitle, {}) +
                            ' pane',
                        });
                        if (isOpened) {
                          this.setState({
                            expanded: difference(expanded, [
                              widget.fieldSetTitle,
                            ]),
                          });
                        } else {
                          expanded.push(widget.fieldSetTitle);
                          this.setState({ expanded });
                        }
                      }}
                    >
                      <SVG src={assetUrl('Icons/arrow-up')} />
                    </button>
                  </Tooltip>
                  {widget.fieldSetHelp && (
                    <p className="widget-help">{t(widget.fieldSetHelp)}</p>
                  )}
                </div>
              )}
              <div>
                <Collapse isOpened={isOpened} className="widget-body">
                  {widget.fields.map((field) => {
                    return this.validateDependencies(field)
                      ? this.renderFiledByType(field)
                      : '';
                  })}
                </Collapse>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

export default FormPanel;
